import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, RefreshCcw, Trash2, Mail, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = {
  label: "", email: "", host: "", port: 993, username: "",
  password: "", ssl: true, active: true,
};

export default function ImapPage() {
  const [accounts, setAccounts] = useState([]);
  const [presets, setPresets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [selectedPreset, setSelectedPreset] = useState("custom");

  const load = async () => {
    const [a, p] = await Promise.all([api.get("/imap/accounts"), api.get("/imap/presets")]);
    setAccounts(a.data); setPresets(p.data);
  };
  useEffect(() => { load(); }, []);

  const applyPreset = (id) => {
    setSelectedPreset(id);
    const p = presets.find(x => x.id === id);
    if (p && p.id !== "custom") {
      setForm(f => ({ ...f, host: p.host, port: p.port, ssl: p.ssl }));
    }
  };

  const save = async () => {
    const payload = { ...form, port: Number(form.port) };
    if (editing && !payload.password) delete payload.password;
    try {
      if (editing) await api.patch(`/imap/accounts/${editing.id}`, payload);
      else {
        if (!payload.password) { toast.error("Passwort fehlt"); return; }
        await api.post("/imap/accounts", payload);
      }
      toast.success(editing ? "Aktualisiert" : "Konto angelegt");
      setOpen(false); setEditing(null); setForm(empty); setSelectedPreset("custom");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("IMAP-Konto entfernen?")) return;
    await api.delete(`/imap/accounts/${id}`); toast.success("Gelöscht"); load();
  };

  const sync = async (id) => {
    toast.message("Synchronisiere…");
    try {
      const { data } = await api.post(`/imap/accounts/${id}/sync`);
      toast.success(`${data.new_items} neue Belege importiert (${data.messages_seen} Mails)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "IMAP-Fehler"); }
  };

  return (
    <div className="space-y-6" data-testid="imap-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Belegeingang per E-Mail</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">IMAP-Konten</h1>
          <p className="text-sm text-neutral-500 mt-2">Verknüpfe deine Beleg-Postfächer. Anhänge landen automatisch in der Inbox „Zur Prüfung&ldquo;.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); setSelectedPreset("custom"); } }}>
          <DialogTrigger asChild><Button data-testid="add-imap-btn"><Plus className="h-4 w-4 mr-2" />Neues Postfach</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Postfach bearbeiten" : "Neues IMAP-Postfach"}</DialogTitle><DialogDescription>Konfiguriere ein E-Mail-Postfach, aus dem Belege automatisch abgeholt werden.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              {!editing && (
                <div>
                  <Label>Anbieter</Label>
                  <Select value={selectedPreset} onValueChange={applyPreset}>
                    <SelectTrigger data-testid="imap-preset"><SelectValue /></SelectTrigger>
                    <SelectContent>{presets.map(p => <SelectItem key={p.id} value={p.id}>{p.label}{p.hint ? ` — ${p.hint}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Bezeichnung</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="z.B. Buchhaltung" data-testid="imap-label" /></div>
                <div className="col-span-2"><Label>E-Mail Adresse</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="imap-email" /></div>
                <div><Label>IMAP-Host</Label><Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="imap-host" /></div>
                <div><Label>Port</Label><Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} data-testid="imap-port" /></div>
                <div><Label>Benutzername</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="imap-username" /></div>
                <div><Label>Passwort / App-Passwort</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Leer lassen = unverändert" : ""} data-testid="imap-password" /></div>
                <div className="flex items-center justify-between border rounded-lg px-4 py-2"><Label className="m-0">SSL/TLS</Label><Switch checked={form.ssl} onCheckedChange={(v) => setForm({ ...form, ssl: v })} /></div>
                <div className="flex items-center justify-between border rounded-lg px-4 py-2"><Label className="m-0">Aktiv</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></div>
              </div>
              <p className="text-xs text-neutral-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                💡 Bei Gmail / Outlook ist ein App-Passwort erforderlich. Das Passwort wird verschlüsselt gespeichert.
              </p>
            </div>
            <DialogFooter><Button onClick={save} data-testid="imap-save">{editing ? "Speichern" : "Anlegen"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white">
        <Table data-testid="imap-table">
          <TableHeader>
            <TableRow>
              <TableHead>Bezeichnung</TableHead><TableHead>E-Mail</TableHead><TableHead>Host</TableHead>
              <TableHead>Letzte Sync</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map((a) => (
              <TableRow key={a.id} className="table-row-hover" data-testid={`imap-row-${a.id}`}>
                <TableCell className="font-medium">{a.label}</TableCell>
                <TableCell className="text-sm text-neutral-600">{a.email}</TableCell>
                <TableCell className="text-sm text-neutral-600">{a.host}:{a.port}</TableCell>
                <TableCell className="text-xs text-neutral-500">{a.last_sync_at ? new Date(a.last_sync_at).toLocaleString("de-DE") : "—"}{a.last_error && <div className="text-red-600 mt-0.5">⚠ {a.last_error}</div>}</TableCell>
                <TableCell><span className="dot inline-block mr-2" style={{ backgroundColor: a.active ? "#10B981" : "#A3A3A3" }} />{a.active ? "Aktiv" : "Pausiert"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => sync(a.id)} title="Jetzt synchronisieren" data-testid={`imap-sync-${a.id}`}><RefreshCcw className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(a); setForm({ ...empty, ...a, password: "" }); setOpen(true); }} data-testid={`imap-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(a.id)} data-testid={`imap-delete-${a.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {accounts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-12"><Mail className="h-8 w-8 mx-auto mb-2 text-neutral-400" />Noch kein Postfach verknüpft</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
